import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AccountButton from "./AccountButton";
import AccountInput from "./AccountInput";
import AccountSection from "./AccountSection";
import ProfileImageUpload from "./ProfileImageUpload";
import {
  getAccountDetails,
  getProfile,
  updateProfile,
  uploadProfilePicture,
} from "./api/apiFunctions";
import "./datepicker-theme.css"; // We'll create this for custom calendar styling
import { t } from "./i18n";
import { DisplayOnCardPill } from "./components/Pills";
import { uploadImage } from "./api/apiFunctions";
import { toast } from "react-toastify";

// Utility function to fix S3 URL region mismatch
const fixS3UrlRegion = (url) => {
  if (!url || typeof url !== "string") return url;

  // Fix the region mismatch: replace us-east-1 with eu-north-1
  // This handles both http:// and https:// URLs
  return url.replace(
    /s3\.us-east-1\.amazonaws\.com/g,
    "s3.eu-north-1.amazonaws.com",
  );
};

// Custom input for react-datepicker styled like AccountInput
const DateInput = React.forwardRef(
  ({ value, onClick, onChange, placeholder }, ref) => (
    <div className="relative flex flex-col mb-4 flex-1 min-w-[180px]">
      <input
        ref={ref}
        value={value}
        onClick={onClick}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border-0 border-b-2 border-[#4962d8] border-t-0 border-r-0 border-l-0 focus:border-b-2 focus:border-[#4962d8] focus:border-t-0 focus:border-r-0 focus:border-l-0 focus:ring-0 text-sm py-1 px-0 bg-transparent text-black font-medium pr-8"
        autoComplete="off"
      />
      <span
        className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center cursor-pointer pr-2"
        onClick={onClick}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" fill="#fff" />
          <path
            d="M16 2v4M8 2v4M3 10h18"
            stroke="#4962d8"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </span>
    </div>
  ),
);

export default function ProfilePage({ lang = "en", goToTab }) {
  const [form, setForm] = useState({
    dob: "",
    height: "",
    weight: "",
    gender: "",
    bloodType: "",
    country: "",
    nationality: "",
    ethnicity: "",
    organDonor: false,
    isSmoker: false,
    // primaryPhone: "",
    // secondaryPhone: "",
    profilePicUrl: "",
    ekgUrl: "",
    // secondaryMobile: "",
    emergencyContacts: [
      { name: "", phone: "", relation: "", allowReleaseMedicalInfo: false },
    ],
    allowReleaseMedicalInfo: false,
    medicalInfoReleaseIndividuals: [],
  });

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [ekgUploading, setEkgUploading] = useState(false);
  const [ekgError, setEkgError] = useState("");

  // Profile image state
  const [profileImg, setProfileImg] = useState(
    "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
  );

  const genderOptions = [
    { value: "", label: t("selectGender", lang) },
    { value: "male", label: t("male", lang) },
    { value: "female", label: t("female", lang) },
    { value: "other", label: t("other", lang) },
  ];
  const bloodTypeOptions = [
    { value: "", label: t("selectBloodType", lang) },
    { value: "A+", label: "A+" },
    { value: "A-", label: "A-" },
    { value: "B+", label: "B+" },
    { value: "B-", label: "B-" },
    { value: "AB+", label: "AB+" },
    { value: "AB-", label: "AB-" },
    { value: "O+", label: "O+" },
    { value: "O-", label: "O-" },
  ];
  const countryOptions = [
    { value: "", label: t("choose", lang) },
    { value: "us", label: "United States" },
    { value: "ca", label: "Canada" },
    { value: "other", label: "Other" },
    // Add more as needed
  ];
  const nationalityOptions = countryOptions;

  const organDonorOptions = [
    { value: true, label: t("yes", lang) },
    { value: false, label: t("no", lang) },
  ];

  const smokerOptions = [
    { value: true, label: t("yes", lang) },
    { value: false, label: t("no", lang) },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch profile data on component mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setErrors({});

        // Get token from localStorage
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");

        if (!token) {
          throw new Error(
            "Authentication token not found. Please login again.",
          );
        }

        // Fetch profile data from API
        const profileResponse = await getProfile(token);

        if (profileResponse.success && profileResponse.data) {
          setForm({
            dob: profileResponse.data.dateOfBirth || "",
            height: profileResponse.data.height || "",
            weight: profileResponse.data.weight || "",
            gender: profileResponse.data.gender || "",
            bloodType: profileResponse.data.bloodType || "",
            country: profileResponse.data.countryOfBirth || "",
            nationality: profileResponse.data.nationality || "",
            ethnicity: profileResponse.data.ethnicity || "",
            organDonor:
              profileResponse.data.organDonor === "true" ||
              profileResponse.data.organDonor === true,
            isSmoker:
              profileResponse.data.isSmoker === "true" ||
              profileResponse.data.isSmoker === true,
            // primaryPhone: profileResponse.data.primaryPhoneNumber || "",
            // secondaryPhone: profileResponse.data.secondaryPhoneNumber || "",
            profilePicUrl: profileResponse.data.profilePicUrl || "",
            ekgUrl: profileResponse.data.ekgUrl || "",
            // secondaryMobile: profileResponse.data.secondaryMobileNumber || "",
            emergencyContacts:
              Array.isArray(profileResponse.data.emergencyContacts) &&
              profileResponse.data.emergencyContacts.length > 0
                ? profileResponse.data.emergencyContacts.map((contact) => ({
                    ...contact,
                    allowReleaseMedicalInfo:
                      contact.allowReleaseMedicalInfo || false,
                  }))
                : [],
            // allowReleaseMedicalInfo:
            //   profileResponse.data.allowReleaseMedicalInfo || false,
            // medicalInfoReleaseIndividuals:
            //   profileResponse.data.medicalInfoReleaseIndividuals || [],
          });
        } else {
          throw new Error("Failed to fetch profile data");
        }

        // Fetch account details to get profile picture
        // const accountResponse = await getAccountDetails(token);
        // if (accountResponse.success && accountResponse.data?.profilePicture) {
        //   const fixedUrl = fixS3UrlRegion(accountResponse.data.profilePicture);
        //   setProfileImg(fixedUrl);
        // }
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setErrors({});
      setSuccess(null);

      // Get token from localStorage
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Prepare profile data for update
      const profileData = {
        dateOfBirth: form.dob,
        height: parseInt(form.height) || 0,
        weight: parseInt(form.weight) || 0,
        gender: form.gender,
        bloodType: form.bloodType,
        nationality: form.nationality,
        countryOfBirth: form.country,
        ethnicity: "not_specified", // Hardcoded value for API
        // primaryPhoneNumber: form.primaryPhone,
        // secondaryPhoneNumber: form.secondaryPhone,
        // secondaryMobileNumber: form.secondaryMobile,
        // Send first contact to maintain backward compatibility with API
        // emergencyContactName: form.emergencyContacts[0]?.name || null,
        // emergencyContactPhone: form.emergencyContacts[0]?.phone || null,
        // emergencyRelation: form.emergencyContacts[0]?.relation || null,
        // Also include entire list for future compatibility
        emergencyContacts: form.emergencyContacts,
        // allowReleaseMedicalInfo: form.allowReleaseMedicalInfo,
        // medicalInfoReleaseIndividuals: form.medicalInfoReleaseIndividuals,
        organDonor: form.organDonor,
        isSmoker: form.isSmoker,
        ekgUrl: form.ekgUrl || "",
        profilePicUrl: form.profilePicUrl || "",
      };

      // Update profile information
      const response = await updateProfile(profileData, token);

      if (!response.success) {
        // Handle specific profile update errors
        const profileErrors = {};
        const message =
          response.message || "Failed to update profile information";

        if (
          message.toLowerCase().includes("date") ||
          message.toLowerCase().includes("birth")
        ) {
          profileErrors.dob = message;
        } else if (message.toLowerCase().includes("height")) {
          profileErrors.height = message;
        } else if (message.toLowerCase().includes("weight")) {
          profileErrors.weight = message;
        } else if (message.toLowerCase().includes("gender")) {
          profileErrors.gender = message;
        } else if (message.toLowerCase().includes("blood")) {
          profileErrors.bloodType = message;
        }
        //  else if (message.toLowerCase().includes("phone")) {
        //   profileErrors.primaryPhone = message;
        // }
        else if (message.toLowerCase().includes("life")) {
          profileErrors.lifeInsurance = message;
        } else if (message.toLowerCase().includes("disability")) {
          profileErrors.disabilityInsurance = message;
        } else if (message.toLowerCase().includes("emergency")) {
          profileErrors.emergencyName = message;
        } else {
          // Default to dob field
          profileErrors.dob = message;
        }

        setErrors(profileErrors);
        return false; // Return false to indicate failure
      }

      // Show success message
      setSuccess("Profile updated successfully!");
      return true; // Return true to indicate success
    } catch (error) {
      // setErrors({
      //   dob:
      //     error.message ||
      //     "Failed to save profile information. Please try again.",
      // });
      return false; // Return false to indicate failure
    } finally {
      setIsSaving(false);
    }
  };

  // EKG file upload handler
  const handleEkgFileChange = async (e) => {
    setEkgError("");
    const file = e.target.files[0];
    if (!file) return;
    setEkgUploading(true);
    try {
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");
      if (!token)
        throw new Error("Authentication token not found. Please login again.");
      const response = await uploadImage(file, token);
      toast.success("Ekg image updated successfully.");
      setForm((prev) => ({ ...prev, ekgUrl: response?.fileUrl }));
    } catch (err) {
      setEkgError("Error uploading EKG file. Please try again.");
    } finally {
      setEkgUploading(false);
    }
  };

  // Convert dob string to Date object for DatePicker
  const dobDate = form.dob ? new Date(form.dob) : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));

    // Clear success message when user starts typing
    if (success) {
      setSuccess(null);
    }
    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Emergency contacts handlers
  const handleEmergencyChange = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.emergencyContacts];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, emergencyContacts: updated };
    });
  };

  // Handler for emergency contact checkbox
  const handleEmergencyContactCheckboxChange = (index, checked) => {
    setForm((prev) => {
      const updated = [...prev.emergencyContacts];
      updated[index] = { ...updated[index], allowReleaseMedicalInfo: checked };
      return { ...prev, emergencyContacts: updated };
    });
  };

  const addEmergencyContact = () => {
    setForm((prev) => ({
      ...prev,
      emergencyContacts: [
        ...prev.emergencyContacts,
        { name: "", phone: "", relation: "", allowReleaseMedicalInfo: false },
      ],
    }));
  };

  const removeEmergencyContact = (index) => {
    setForm((prev) => ({
      ...prev,
      emergencyContacts: prev.emergencyContacts.filter((_, i) => i !== index),
    }));
  };

  // Handle profile image upload
  const handleProfileImgChange = async (file, previewUrl) => {
    // Show preview immediately
    if (previewUrl) {
      setProfileImg(previewUrl);
    }

    // Upload the file
    if (file instanceof File) {
      try {
        setIsUploadingImage(true);

        // Get token from localStorage
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");

        if (!token) {
          throw new Error(
            "Authentication token not found. Please login again.",
          );
        }

        // Upload the file
        const response = await uploadProfilePicture(file, token);

        if (response.success) {
          // Determine the profile picture URL from response
          let profilePicUrl =
            response.data?.profilePicture || response.data?.url;
          let fixedUrl = null;

          // If the API returns the new URL, use it (fixing region if needed)
          if (profilePicUrl) {
            fixedUrl = fixS3UrlRegion(profilePicUrl);
            setProfileImg(fixedUrl);
          } else {
            // Keep the preview if API doesn't return URL
            // The image should be available via getAccountDetails after upload
          }

          setSuccess("Profile picture updated successfully!");

          // Dispatch custom event to notify other components (like PatientSidebar) to refresh
          // If we have a URL, include it; otherwise PatientSidebar will refetch
          window.dispatchEvent(
            new CustomEvent("profilePictureUpdated", {
              detail: { profilePictureUrl: fixedUrl || profilePicUrl },
            }),
          );
        } else {
          throw new Error(
            response.message || "Failed to upload profile picture",
          );
        }
      } catch (error) {
        console.error("Error uploading profile picture:", error);
        setErrors({
          profilePicture:
            error.message ||
            "Failed to upload profile picture. Please try again.",
        });
        // Revert to previous image on error - fetch current from server
        try {
          const token =
            localStorage.getItem("authToken") ||
            sessionStorage.getItem("authToken");
          if (token) {
            const accountResponse = await getAccountDetails(token);
            if (
              accountResponse.success &&
              accountResponse.data?.profilePicture
            ) {
              const fixedUrl = fixS3UrlRegion(
                accountResponse.data.profilePicture,
              );
              setProfileImg(fixedUrl);
            }
          }
        } catch (fetchError) {
          console.error("Error reverting image:", fetchError);
        }
      } finally {
        setIsUploadingImage(false);
      }
    }
  };

  // DatePicker change handler
  const handleDateChange = (date) => {
    setForm((f) => ({
      ...f,
      dob: date
        ? `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
            date.getDate(),
          ).padStart(2, "0")}/${date.getFullYear()}`
        : "",
    }));
  };

  // Show loading state
  if (loading) {
    return (
      <div className="h-screen bg-[#f7f8fa] flex flex-col items-center justify-center pb-8">
        <div className="w-full max-w-4xl">
          <div className="bg-white rounded-xl shadow p-8 mt-4">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4962d8]"></div>
              <span className="ml-4 text-lg text-[#2d348f]">
                Loading profile data...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center p-4 md:p-8">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          <AccountSection>
            {t("profileData", lang) || "Profile Data"}
          </AccountSection>
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="text-green-600 text-sm">✅ {success}</div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 items-center">
            {/* Profile image upload */}
            <div className="flex flex-col justify-center items-center h-full">
              <ProfileImageUpload
                image={profileImg}
                onChange={handleProfileImgChange}
                size={96}
              />
              {isUploadingImage && (
                <div className="mt-2 text-sm text-blue-600">Uploading...</div>
              )}
              {errors.profilePicture && (
                <div className="mt-2 text-sm text-red-500">
                  ⚠️ {errors.profilePicture}
                </div>
              )}
            </div>
            {/* Date of Birth with react-datepicker and custom input styled like AccountInput */}
            <div className="flex flex-col mb-4 flex-1 min-w-[180px] justify-center">
              <label className="text-xs font-semibold text-[#837f90] mb-1">
                {t("dob", lang) || "Date of Birth"}
              </label>
              <DatePicker
                selected={dobDate}
                onChange={handleDateChange}
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/DD/YYYY"
                calendarClassName="custom-datepicker"
                popperPlacement="bottom"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
                name="dob"
                autoComplete="off"
                customInput={<DateInput />}
              />
            </div>
            <div>
              <AccountInput
                label={t("height", lang) || "Height (inches)"}
                name="height"
                value={form.height}
                onChange={handleChange}
              />
              {errors.height && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.height}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("weight", lang) || "Weight (pounds)"}
                name="weight"
                value={form.weight}
                onChange={handleChange}
              />
              {errors.weight && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.weight}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("gender", lang) || "Gender"}
              </label>
              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {genderOptions.map((opt) => (
                  <option
                    className="overflow-hidden"
                    key={opt.value}
                    value={opt.value}
                  >
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.gender && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.gender}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("bloodType", lang) || "Blood Type"}
              </label>
              <select
                name="bloodType"
                value={form.bloodType}
                onChange={handleChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {bloodTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.bloodType && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.bloodType}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("countryOfBirth", lang) || "Country of Birth"}
              </label>
              <select
                name="country"
                value={form.country}
                onChange={handleChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {countryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("nationality", lang) || "Nationality"}
              </label>
              <select
                name="nationality"
                value={form.nationality}
                onChange={handleChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {nationalityOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("organDonor", lang) || "Organ Donor"}
              </label>
              <select
                name="organDonor"
                value={form.organDonor || false}
                onChange={handleChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {organDonorOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("smoker", lang) || "Smoker"}
              </label>
              <select
                name="isSmoker"
                value={form.isSmoker || false}
                onChange={handleChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              >
                {smokerOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {/* <div>
              <AccountInput
                label={t("primaryPhone", lang) || "Primary Phone Number"}
                name="primaryPhone"
                value={form.primaryPhone}
                onChange={handleChange}
                isPhoneInput
              />
              {errors.primaryPhone && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.primaryPhone}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("secondaryPhone", lang) || "Secondary Phone Number"}
                name="secondaryPhone"
                value={form.secondaryPhone}
                onChange={handleChange}
                isPhoneInput
              />
              {errors.secondaryPhone && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.secondaryPhone}
                </div>
              )}
            </div> */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                EKG Image
              </label>
              <div>
                {form.ekgUrl ? (
                  <img
                    src={form.ekgUrl}
                    alt="EKG"
                    style={{
                      maxWidth: 200,
                      maxHeight: 120,
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      document.getElementById("ekg-file-input")?.click();
                    }}
                  />
                ) : (
                  <label
                    className={`border rounded p-2 text-sm w-full cursor-pointer flex items-center justify-center ${ekgError ? "border-red-500" : "border-gray-300"} hover:border-[#4962d8] transition-colors`}
                  >
                    <span className="text-gray-600 truncate flex-1 text-center">
                      Upload EKG Image
                    </span>
                    <input
                      type="file"
                      name="ekg"
                      id="ekg-file-input"
                      onChange={handleEkgFileChange}
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      className="hidden"
                    />
                  </label>
                )}
                {form.ekgUrl && (
                  <input
                    type="file"
                    name="ekg"
                    id="ekg-file-input"
                    onChange={handleEkgFileChange}
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    className="hidden"
                  />
                )}
                {ekgUploading && (
                  <div className="mt-2 text-sm text-blue-600">Uploading...</div>
                )}
                {ekgError && (
                  <div className="mt-2 text-sm text-red-500">⚠️ {ekgError}</div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between mb-2">
            <AccountSection>
              {t("emergencyContact", lang) || "Emergency Contacts"}
            </AccountSection>
            <button
              type="button"
              onClick={addEmergencyContact}
              className="h-9 w-9 flex items-center justify-center rounded-md bg-[#4962d8] text-white hover:bg-[#2d348f] transition-colors"
              title="Add contact"
            >
              +
            </button>
          </div>
          <div className="mb-4">
            {form.emergencyContacts.map((c, index) => (
              <div
                key={index}
                className="mb-6 pb-6 border-b border-gray-200 last:border-b-0"
              >
                {index === 0 && (
                  <div className="pb-6">
                    <DisplayOnCardPill />
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                  <div>
                    <AccountInput
                      label={t("contactName", lang) || "Contact Name"}
                      name={`emergencyName_${index}`}
                      value={c.name}
                      onChange={(e) =>
                        handleEmergencyChange(index, "name", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <AccountInput
                      label={t("contactPhone", lang) || "Contact Phone"}
                      name={`emergencyPhone_${index}`}
                      value={c.phone}
                      onChange={(e) =>
                        handleEmergencyChange(index, "phone", e.target.value)
                      }
                      isPhoneInput
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <AccountInput
                        label={t("relation", lang) || "Relation"}
                        name={`emergencyRelation_${index}`}
                        value={c.relation}
                        onChange={(e) =>
                          handleEmergencyChange(
                            index,
                            "relation",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    {form.emergencyContacts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmergencyContact(index)}
                        className="h-9 px-3 mb-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                        title="Remove"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                {/* Checkbox for each emergency contact - only show if at least one field has a value */}
                {(c.name?.trim() || c.phone?.trim() || c.relation?.trim()) && (
                  <div className="mt-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={c.allowReleaseMedicalInfo || false}
                        onChange={(e) =>
                          handleEmergencyContactCheckboxChange(
                            index,
                            e.target.checked,
                          )
                        }
                        className="w-4 h-4 text-[#4962d8] border-gray-300 rounded focus:ring-[#4962d8] focus:ring-2 mr-2"
                      />
                      <span className="text-sm text-gray-700">
                        Allow to release any and all medical information to this
                        individual
                      </span>
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <AccountButton
              className="md:w-1/3"
              disabled={isSaving}
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> Back
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={isSaving}
            >
              <span className="ml-2">Next &raquo;</span>
            </AccountButton>
          </div>
        </div>
      </div>
    </div>
  );
}
